import React, { useState, useRef, useEffect, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Plus, X, Search, Maximize2 } from "lucide-react";

const AutocompleteInput = ({
  value,
  onChange,
  onSelect,
  suggestions = [],
  placeholder = "",
  className = "",
  disabled = false,
  "data-testid": testId
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [showAddNew, setShowAddNew] = useState(false);
  const [popupSize, setPopupSize] = useState({ width: 400, height: 280 });
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const popupRef = useRef(null);
  const containerRef = useRef(null);

  // Calculate popup position - ALWAYS below the input, never covering it
  const updatePopupPosition = useCallback(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      let left = rect.left;
      // Always position below the input with a small gap
      let top = rect.bottom + 6;
      
      // Calculate available space below
      const spaceBelow = viewportHeight - rect.bottom - 20;
      
      // Adjust popup height if not enough space below (but never go above input)
      const adjustedHeight = Math.min(popupSize.height, Math.max(150, spaceBelow));
      
      // Adjust if popup would go off right edge
      if (left + popupSize.width > viewportWidth - 20) {
        left = Math.max(10, viewportWidth - popupSize.width - 20);
      }
      
      // Ensure left is not negative
      left = Math.max(10, left);
      
      setPopupPosition({ 
        top: Math.max(rect.bottom + 6, top), 
        left,
        adjustedHeight 
      });
    }
  }, [popupSize]);

  // Filter suggestions based on input
  useEffect(() => {
    if (value && value.trim().length > 0) {
      const trimmedValue = value.trim().toLowerCase();
      const filtered = suggestions.filter(
        (s) => s && s.toLowerCase().includes(trimmedValue)
      );
      setFilteredSuggestions(filtered.slice(0, 25));
      
      const exactMatch = suggestions.some(
        (s) => s && s.toLowerCase() === trimmedValue
      );
      
      setShowAddNew(!exactMatch && value.trim().length > 0);
    } else {
      setFilteredSuggestions([]);
      setShowAddNew(false);
    }
    setHighlightedIndex(-1);
  }, [value, suggestions]);

  // Update position when opening
  useEffect(() => {
    if (isOpen) {
      updatePopupPosition();
    }
  }, [isOpen, updatePopupPosition]);

  const totalItems = filteredSuggestions.length + (showAddNew ? 1 : 0);

  const handleKeyDown = (e) => {
    if (!isOpen) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < totalItems - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0) {
          let selectedValue;
          if (highlightedIndex < filteredSuggestions.length) {
            selectedValue = filteredSuggestions[highlightedIndex];
          } else if (showAddNew) {
            selectedValue = value.trim();
          }
          if (selectedValue) {
            onChange(selectedValue);
            if (onSelect) onSelect(selectedValue);
          }
          setIsOpen(false);
        }
        break;
      case "Escape":
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (listRef.current && highlightedIndex >= 0) {
      // Find the highlighted item by data-index attribute
      const item = listRef.current.querySelector(`[data-suggestion-index="${highlightedIndex}"]`);
      if (item) {
        item.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    }
  }, [highlightedIndex]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target) &&
          popupRef.current && !popupRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Handle resize
  const handleResizeStart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = popupSize.width;
    const startHeight = popupSize.height;
    
    const handleResize = (moveEvent) => {
      const newWidth = Math.max(350, Math.min(800, startWidth + (moveEvent.clientX - startX)));
      const newHeight = Math.max(250, Math.min(600, startHeight + (moveEvent.clientY - startY)));
      setPopupSize({ width: newWidth, height: newHeight });
    };
    
    const handleResizeEnd = () => {
      document.removeEventListener('mousemove', handleResize);
      document.removeEventListener('mouseup', handleResizeEnd);
    };
    
    document.addEventListener('mousemove', handleResize);
    document.addEventListener('mouseup', handleResizeEnd);
  };

  const handleAddNew = () => {
    onChange(value.trim());
    if (onSelect) onSelect(value.trim());
    setIsOpen(false);
  };

  const handleSelect = (suggestion) => {
    onChange(suggestion);
    if (onSelect) onSelect(suggestion);
    setIsOpen(false);
  };

  const openPopup = () => {
    if (value && value.trim().length > 0 && (filteredSuggestions.length > 0 || showAddNew)) {
      setIsOpen(true);
    }
  };

  return (
    <div className="relative" ref={containerRef}>
      <div className="flex gap-1">
        <Input
          ref={inputRef}
          value={value || ""}
          onChange={(e) => {
            onChange(e.target.value);
            if (e.target.value.trim().length > 0) {
              setIsOpen(true);
            }
          }}
          onFocus={openPopup}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={className}
          disabled={disabled}
          data-testid={testId}
        />
      </div>
      
      {isOpen && (filteredSuggestions.length > 0 || showAddNew) && (
        <div
          ref={popupRef}
          className="fixed z-[200] bg-white border border-slate-300 rounded-lg shadow-2xl overflow-hidden"
          style={{
            width: popupSize.width,
            height: popupPosition.adjustedHeight || popupSize.height,
            top: popupPosition.top,
            left: popupPosition.left,
            maxHeight: 'calc(100vh - 100px)'
          }}
        >
          {/* Header - more compact */}
          <div className="flex items-center justify-between px-3 py-1.5 bg-gradient-to-r from-blue-50 to-slate-50 border-b">
            <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
              <Search className="w-3.5 h-3.5 text-blue-500" />
              <span className="text-xs">Seleccionar</span>
              <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                {filteredSuggestions.length}
              </span>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="p-1 hover:bg-slate-200 rounded-full transition-colors"
            >
              <X className="w-3.5 h-3.5 text-slate-500" />
            </button>
          </div>
          
          {/* Search preview - compact */}
          <div className="px-3 py-1.5 bg-slate-50 border-b text-xs text-slate-600">
            Buscando: <span className="font-medium text-slate-800">"{value}"</span>
          </div>
          
          {/* List */}
          <div 
            ref={listRef}
            className="overflow-auto"
            style={{ height: (popupPosition.adjustedHeight || popupSize.height) - 90 }}
          >
            {filteredSuggestions.map((suggestion, index) => (
              <div
                key={suggestion}
                data-suggestion-index={index}
                className={`px-3 py-2 cursor-pointer border-b border-slate-100 transition-all ${
                  index === highlightedIndex
                    ? "bg-blue-100 text-blue-900 border-l-4 border-l-blue-500"
                    : "hover:bg-slate-50 border-l-4 border-l-transparent"
                }`}
                onClick={() => handleSelect(suggestion)}
              >
                <div className="text-sm break-words whitespace-normal leading-snug">
                  {suggestion}
                </div>
              </div>
            ))}
            
            {showAddNew && (
              <div
                data-suggestion-index={filteredSuggestions.length}
                className={`px-3 py-2 cursor-pointer border-t-2 border-green-200 flex items-start gap-2 ${
                  highlightedIndex === filteredSuggestions.length
                    ? "bg-green-100 text-green-900"
                    : "hover:bg-green-50 text-green-700"
                }`}
                onClick={handleAddNew}
              >
                <Plus className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div className="text-sm leading-snug">
                  <span>Agregar: </span>
                  <strong className="break-words">"{value.trim()}"</strong>
                </div>
              </div>
            )}
          </div>
          
          {/* Footer - compact */}
          <div className="flex items-center justify-between px-2 py-1.5 bg-slate-100 border-t text-[10px] text-slate-500">
            <span>↑↓ navegar · Enter seleccionar · Esc cerrar</span>
            <div 
              className="cursor-se-resize p-0.5 hover:bg-slate-200 rounded flex items-center gap-1 select-none"
              onMouseDown={handleResizeStart}
            >
              <Maximize2 className="w-2.5 h-2.5" />
            </div>
          </div>
          
          {/* Resize handle corner */}
          <div
            className="absolute bottom-0 right-0 w-5 h-5 cursor-se-resize"
            onMouseDown={handleResizeStart}
            style={{
              background: 'linear-gradient(135deg, transparent 40%, #94a3b8 40%, #94a3b8 50%, transparent 50%, transparent 60%, #94a3b8 60%, #94a3b8 70%, transparent 70%)',
            }}
          />
        </div>
      )}
    </div>
  );
};

export default AutocompleteInput;
